import React, { useState, useEffect } from "react";
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    message,
    Spin,
} from "antd";
import {
    PlusOutlined,
    SearchOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "../json/appConstant.json";

const PropertyCategories = ({ isDarkMode }) => {
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isDataLoading, setIsDataLoading] = useState(false);

    const { jsonApi } = useApis();

    useEffect(() => {
        fetchCategories(pagination.current, pagination.pageSize, search);
    }, [pagination.current, pagination.pageSize, search]);

    const fetchCategories = async (page, pageSize, search = "") => {
        setIsDataLoading(true);
        const params = {
            page,
            pageSize,
            search,
        };

        const response = await jsonApi(appConstant.PropertyCategory, params, {}, null);

        if (response?.data?.code === 200 && response?.data?.status === "success") {
            const data = response.data.data.categories || response.data.data;
            setCategories(
                data.map((cat) => ({
                    key: cat.id,
                    id: cat.id,
                    name: cat.name,
                    categoryCode: cat.categoryCode,
                    createdAt: cat.createdAt,
                }))
            );
            setPagination((prev) => ({
                ...prev,
                total: response.data.data.total || data.length,
            }));
        }
        setIsDataLoading(false);
    };

    const handleAddCategory = async (values) => {
        setLoading(true);
        try {
            const response = await jsonApi(
                appConstant.createPropertyCategory,
                null,
                { name: values.name },
                null
            );

            if (response.data.code === 200) {
                message.success("Category added successfully");
                fetchCategories(pagination.current, pagination.pageSize);
                setIsModalVisible(false);
                form.resetFields();
            } else {
                message.error(response.message || "Failed to add category");
            }
        } catch (error) {
            console.error("Add Category Error:", error);
            message.error("An error occurred");
        }
        setLoading(false);
    };

    const columns = [
        {
            title: "S.No",
            key: "serialNumber",
            render: (_, __, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1,
            
        },
        {
            title: "Category Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Category Code",
            dataIndex: "categoryCode",
            key: "categoryCode",
            // sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => new Date(date).toLocaleDateString(), 
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt), 
            width: "20%",
          }
        
    ];

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                }}
            >
                <h2 style={{ color: isDarkMode ? "#fff" : "#000" }}>
                    Property Categories
                </h2>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalVisible(true)}
                >
                    Add Category
                </Button>
            </div>

            <Input
                placeholder="Search categories..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                suffix={
                    search && (
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setSearch("");
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        />
                    )
                }
                style={{ width: 300, marginBottom: 16 }}
            />

            <Card
                style={{
                    background: isDarkMode ? "#1f1f1f" : "#fff",
                }}
            >
                <Spin spinning={isDataLoading}>
                    <Table
                        columns={columns}
                        dataSource={categories}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                            onChange: (page, pageSize) => {
                                setPagination({ current: page, pageSize });
                            },
                        }}
                        rowKey="id"
                    />
                </Spin>
            </Card>

            <Modal
                visible={isModalVisible}
                title="Add Property Category"
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setIsModalVisible(false);
                            form.resetFields();
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={() => form.submit()}
                    >
                        Add
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical" onFinish={handleAddCategory}>
                    <Form.Item
                        name="name"
                        label="Category Name"
                        rules={[
                            { required: true, message: "Please enter a category name" },
                            { max: 50, message: "Maximum 50 characters allowed" },
                            {
                                pattern: /^[A-Za-z\s]+$/,
                                message: "Only alphabets and spaces are allowed",
                            },
                        ]}
                    >
                        <Input placeholder="Enter category name" />
                    </Form.Item>

                    <Form.Item
                        name="categoryCode"
                        label="Category Code"
                        rules={[
                            { required: true, message: "Please enter a category code" },
                            { max: 50, message: "Maximum 50 characters allowed" },
                           
                        ]}
                    >
                        <Input placeholder="Enter category code" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PropertyCategories;
